/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  Row,
  Spin,
  Form,
  Modal,
  Button,
  DatePicker,
  Input,
  Col,
  message,
} from 'antd';
import { CheckCircleOutlined } from '@ant-design/icons';
import moment from 'moment';

// Utils
import { formatReceivedValues } from 'utils/formatValues'; // Asegúrate de tener este util o usa formatReceived
import { onError, onSuccess } from 'utils/handlers';
import currencyFormatter from 'utils/currencyFormatter';
import {
  ESTATUS_ACTIVO,
  ESTATUS_AUTORIZADO,
} from 'utils/constants';

// Components
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import ModalConfirmation from 'components/ModalConfirmation';
import Select from 'components/Select';
import CurrencyInput from 'components/CurrencyInput';

// APIs (Se asume que crearás estas rutas en tu archivo de API)
import {
  getSolicitudesPlazasDetalles, // GET /api/nominas/solicitudes-plazas-detalles/?solicitud_plaza=ID
  postSolicitudPlazaDetalle,
  patchSolicitudPlazaDetalle,
  deleteSolicitudPlazaDetalle,
  autorizarSolicitudPlazaDetalle,
} from 'api/nominas/solicitudes/solicitudesPlazasDetalle';
import { getPlazas } from 'api/catalogos/plazas'; // Catálogo de plazas (puestos reales)

const SolicitudesPlazasDetalles = ({
  solicitudPlazaId, // ID del padre (SolicitudPlaza)
  permission,
  estatusInactivoPadre, // Si el padre está inactivo/autorizado, bloqueamos aquí
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  // Estado de la tabla
  const [data, setData] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  // Catalogos
  const [plazasCatalog, setPlazasCatalog] = useState([]);

  // Modales
  const [open, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [visibleAutorizar, setVisibleAutorizar] = useState(false);
  const [estatusVisual, setEstatusVisual] = useState('Activo');

  // Datos seleccionados
  const [selectedData, setSelectedData] = useState({});

  // --- CARGA DE DATOS ---
  const fetchData = async () => {
    if (!solicitudPlazaId) return;
    setLoading(true);
    try {
      const _data = await getSolicitudesPlazasDetalles({ solicitud_plaza: solicitudPlazaId });
      // Se asume que el backend devuelve un array de objetos con la estructura necesaria
      setData(_data);
    } catch (error) {
      onError(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCatalogos = async () => {
    try {
      // Cargar plazas activas para el select
      const _plazas = await getPlazas({ estados_globales: ESTATUS_ACTIVO });
      setPlazasCatalog(_plazas);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[Solicitudes de Plazas Detalles] error al cargar el catálogo de plazas', error);
    }
  };

  useEffect(() => {
    if (solicitudPlazaId) {
      fetchData();
      fetchCatalogos();
    }
  }, [solicitudPlazaId]);

  // --- ACCIONES ---

  const onCancel = () => {
    setVisible(false);
    setVisibleAutorizar(false);
    form.resetFields();
    setSelectedRowKeys([]);
    setSelectedData({});
    setEstatusVisual('Activo');
  };

  const onClickAdd = () => {
    form.resetFields();
    setEstatusVisual('Activo');
    setVisible(true);
  };

  const onClickEdit = () => {
    if (!selectedRowKeys.length) return;
    const record = data.find((d) => d.id === selectedRowKeys[0]);
    if (record) {
      form.setFieldsValue({
        ...record,
        vigencia_final: record.vigencia_final ? moment(record.vigencia_final) : null,
      });
      // Ajuste visual estatus
      setEstatusVisual(record.vigencia_final ? 'Inactivo' : 'Activo');
      setVisible(true);
    }
  };

  const onFinish = async () => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = form.getFieldsValue();

      const payload = {
        ...values,
        solicitud_plaza: solicitudPlazaId,
        vigencia_final: values.vigencia_final ? values.vigencia_final.format('YYYY-MM-DD') : null,
        estados_globales: ESTATUS_ACTIVO, // Siempre nace activo
      };

      const [key] = selectedRowKeys;
      const success = await (key
        ? patchSolicitudPlazaDetalle(key, payload)
        : postSolicitudPlazaDetalle(payload)
      );

      if (success) {
        onCancel();
        fetchData();
        onSuccess({}, key ? 'Actualizado correctamente' : 'Agregado correctamente');
      }
    } catch (err) {
      onError(err, setLoading);
    } finally {
      setLoading(false);
    }
  };

  const deleteItem = async () => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      if (key) {
        await deleteSolicitudPlazaDetalle(key);
        setVisibleAlert(false);
        onCancel();
        fetchData();
        onSuccess({}, 'Eliminado correctamente');
      }
    } catch (err) {
      onError(err, setLoading);
    } finally {
      setLoading(false);
    }
  };

  const onConfirmAutorizar = async () => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      await autorizarSolicitudPlazaDetalle(key);
      setVisibleAutorizar(false);
      fetchData();
      onSuccess({}, 'Autorizado correctamente');
    } catch (err) {
      onError(err, setLoading);
    } finally {
      setLoading(false);
    }
  };

  // --- HANDLERS TABLA ---
  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    setSelectedData(record);
  };

  const onDateChange = (date) => {
    setEstatusVisual(date ? 'Inactivo' : 'Activo');
  };

  // --- COLUMNAS (Según imagen image_5ac884.png) ---
  const columns = [
    {
      titleText: 'Plaza',
      dataIndex: 'plaza_descripcion', // Asume que el serializer manda la descripción concatenada
      key: 'plaza_descripcion',
      width: 250,
      render: (val, record) => val || `${record.plaza_clave || ''} - ${record.plaza_nombre || ''}`,
    },
    {
      titleText: 'Puesto',
      dataIndex: 'puesto_descripcion',
      key: 'puesto_descripcion',
      width: 200,
    },
    {
      titleText: 'Área',
      dataIndex: 'area_descripcion',
      key: 'area_descripcion',
      width: 250,
    },
    {
      titleText: 'Importe',
      dataIndex: 'importe',
      key: 'importe',
      width: 150,
      render: (val) => currencyFormatter(val || 0),
    },
    {
      titleText: 'Vigencia Final',
      dataIndex: 'vigencia_final',
      key: 'vigencia_final',
      width: 150,
      render: (val) => (val ? moment(val).format('DD/MM/YYYY') : ''),
    },
    {
      titleText: 'Estatus',
      dataIndex: 'estatus_descripcion',
      key: 'estatus_descripcion',
      width: 150,
    },
  ];

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
    onChange: (keys) => setSelectedRowKeys(keys),
  };

  // Determinar si deshabilitamos controles (si padre está bloqueado o registro autorizado)
  const isReadOnly = estatusInactivoPadre || selectedData?.estados_globales === ESTATUS_AUTORIZADO;

  return (
    <div style={{ marginTop: '20px', borderTop: '1px solid #f0f0f0', paddingTop: '10px' }}>
      <p style={{ color: '#D88F4F', fontWeight: 600 }}>Detalle de Plazas</p>

      <Spin spinning={loading}>
        <Table
          cols={columns}
          data={data}
          rowSelection={rowSelection}
          handleOnRowClick={handleOnRowClick}
          controls={{
            onClickAdd,
            onClickEdit,
            onClickDelete: () => setVisibleAlert(true),
          }}
          customActions={[
            {
              onClick: () => setVisibleAutorizar(true),
              permission: { model: 'solicitudplazadetalle', action: 'autorizar' },
              text: 'Autorizar',
              icon: <CheckCircleOutlined />,
              className: 'success',
              // eslint-disable-next-line max-len
              disabled: !selectedRowKeys.length || selectedData?.estados_globales === ESTATUS_AUTORIZADO,
            },
          ]}
          disableAdd={estatusInactivoPadre}
          disableDelete={!selectedRowKeys.length || isReadOnly}
          // El grid hijo suele ser más compacto
          // scroll={{ x: 1000 }}
          permission={permission}
          titleTable=""
        />
      </Spin>

      {/* --- MODAL FORMULARIO DETALLE --- */}
      <Modal
        open={open}
        width="80%"
        title={(
          <FormSubmitControls
            label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Plaza Detalle`}
            onFinish={onFinish}
            onCancel={onCancel}
            loading={loading}
          />
          )}
        footer={null}
        onCancel={onCancel}
        closable={false}
        maskClosable={false}
      >
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item
                name="plaza"
                label="Plaza"
                rules={[{ required: true, message: 'Requerido' }]}
              >
                <Select
                  dataSource={plazasCatalog}
                  keyProp="id"
                  labelProp="descripcion_completa"
                  showSearch
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <CurrencyInput
                name="importe"
                label="Importe"
                rules={[{ required: true, message: 'Requerido' }]}
              />
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="vigencia_final" label="Vigencia Final">
                <DatePicker
                  style={{ width: '100%' }}
                  format="DD/MM/YYYY"
                  onChange={onDateChange}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item label="Estatus">
                <Input value={estatusVisual} disabled style={{ backgroundColor: '#f5f5f5' }} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item hidden>
            <Button htmlType="submit" />
          </Form.Item>
        </Form>
      </Modal>

      {/* --- ALERTAS --- */}
      <ModalDelete
        open={visibleAlert}
        onCancel={() => setVisibleAlert(false)}
        onDelete={deleteItem}
        content="¿Eliminar el detalle seleccionado?"
        loading={loading}
      />
      <ModalConfirmation
        open={visibleAutorizar}
        onCancel={() => setVisibleAutorizar(false)}
        onFinish={onConfirmAutorizar}
        contentTitle="Autorizar Detalle"
        ConfirmAutorize
        loading={loading}
      />
    </div>
  );
};

export default SolicitudesPlazasDetalles;
